import * as React from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import { Building2Icon, ChevronDownIcon, LandmarkIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, PencilIcon, ShieldIcon } from 'lucide-react';

import { useUser } from '@/components/auth/user-provider';
import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { useManagedMode } from '@/components/layout/managed-mode-provider';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { firstTenantAdministrationPage } from '@/features/tenancy/administration-navigation';
import { defaultEntity, selectedEntity } from '@/features/tenancy/context-navigation';
import { hasTenantPermission } from '@/features/tenancy/permissions';
import { tenantPath, tenantSlugFromPath } from '@/features/tenancy/urls';
import { useAppRoutes } from '@/hooks/use-app-routes';
import { useNavGroupState } from '@/hooks/use-nav-group-state';
import { BUILT_IN_NAVIGATION_LAYOUT, createGroupedNavigation, navLinkPath, type GroupedNavigation, type NavGroup, type NavItem, type NavSection } from '@/lib/navigation';
import { cn } from '@/lib/utils';

function isNavigationRoot(path: string): boolean {
  return path === '/' || /^\/t\/[^/]+\/?$/.test(path);
}

// Keep the NavLink class callback inside the ref-forwarding component: the
// tooltip's asChild slot merges className as a string on its direct child.
const SidebarLinkTarget = React.forwardRef<HTMLAnchorElement,
  Omit<React.ComponentPropsWithoutRef<'a'>, 'href' | 'children'> & { item: NavItem; collapsed: boolean }
>(function SidebarLinkTarget({ item, collapsed, className, ...props }, ref) {
  return (
    <NavLink
      {...props}
      ref={ref}
      to={item.to}
      end={isNavigationRoot(item.to)}
      aria-label={collapsed ? item.title : undefined}
      className={({ isActive }) =>
        cn(
          'focus-visible:ring-sidebar-ring flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar focus-visible:outline-none motion-reduce:transition-none',
          isActive
            ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
            : 'bg-sidebar-item text-sidebar-item-foreground hover:bg-sidebar-item-accent hover:text-sidebar-item-accent-foreground',
          collapsed && 'justify-center',
          className
        )
      }
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
    </NavLink>
  );
});

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const link = <SidebarLinkTarget item={item} collapsed={collapsed} />;
  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.title}</TooltipContent>
    </Tooltip>
  );
}

function SidebarSection({
  section,
  collapsed,
  group,
  open = true,
  onToggle
}: {
  section: NavSection;
  collapsed: boolean;
  group?: NavGroup;
  open?: boolean;
  onToggle?: () => void;
}) {
  const contentId = React.useId();
  return (
    <div className="flex flex-col gap-0.5">
      {!collapsed && group ? (
        <Button
          type="button"
          variant="plain"
          size="auto"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={onToggle}
          className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring flex w-full items-center justify-between rounded-md px-2 py-1 has-[>svg]:px-2 text-left text-xs font-medium tracking-wide uppercase focus-visible:ring-2 focus-visible:outline-none"
        >
          <span className="truncate">{section.heading}</span>
          <ChevronDownIcon aria-hidden className={cn('size-3.5 shrink-0 transition-transform motion-reduce:transition-none', !open && '-rotate-90')} />
        </Button>
      ) : !collapsed && section.heading !== 'App' && (
        <p className="text-sidebar-foreground px-2 py-1 text-xs font-medium tracking-wide uppercase">
          {section.heading}
        </p>
      )}
      <div id={contentId} hidden={!collapsed && !open} className="flex flex-col gap-0.5">
        {section.items.map((item) => (
          <SidebarLink key={item.to} item={item} collapsed={collapsed} />
        ))}
      </div>
    </div>
  );
}

function itemMatchesPath(item: NavItem, pathname: string): boolean {
  return Boolean(matchPath({ path: item.to, end: isNavigationRoot(item.to) }, pathname));
}

export function SidebarNavigation({
  navigation,
  collapsed,
  scopeKey,
  pathname
}: {
  navigation: GroupedNavigation;
  collapsed: boolean;
  scopeKey: string | null;
  pathname: string;
}) {
  const { isOpen, setOpen } = useNavGroupState(scopeKey);
  const activeGroupId = navigation.groups.find((group) =>
    group.items.some((item) => itemMatchesPath(item, pathname)))?.id;

  React.useEffect(() => {
    if (activeGroupId && !isOpen(activeGroupId, true)) setOpen(activeGroupId, true);
  }, [activeGroupId, pathname, scopeKey, isOpen, setOpen]);

  if (!navigation.enabled) return navigation.flat.map((section) => (
    <SidebarSection key={section.heading} section={section} collapsed={collapsed} />
  ));

  const primaryItems = [...(navigation.primary ? [navigation.primary] : []), ...navigation.pinnedAfterHome];
  const sections: React.ReactNode[] = primaryItems.length
    ? [<SidebarSection key="primary-home" section={{ heading: 'App', items: primaryItems }} collapsed={collapsed} />]
    : [];
  for (const group of navigation.groups) {
    if (collapsed && sections.length) sections.push(<Separator key={`divider-${group.id}`} className="bg-sidebar-border my-1" data-nav-group-divider />);
    sections.push(
      <SidebarSection
        key={group.id}
        section={group}
        group={group}
        collapsed={collapsed}
        open={isOpen(group.id, group.defaultCollapsed)}
        onToggle={() => setOpen(group.id, !isOpen(group.id, group.defaultCollapsed))}
      />
    );
  }
  if (navigation.unassigned.length) {
    if (collapsed && sections.length) sections.push(<Separator key="divider-unassigned" className="bg-sidebar-border my-1" data-nav-group-divider />);
    sections.push(<SidebarSection key="unassigned" section={{ heading: 'App', items: navigation.unassigned }} collapsed={collapsed} />);
  }
  const settingsItems = [...(navigation.settings ? [navigation.settings] : []), ...navigation.pinnedAfterSettings];
  if (settingsItems.length) {
    if (sections.length) sections.push(<Separator key="divider-settings" className="bg-sidebar-border my-1" data-nav-group-divider={collapsed || undefined} />);
    sections.push(<SidebarSection key="settings" section={{ heading: 'App', items: settingsItems }} collapsed={collapsed} />);
  }
  return sections;
}

function SidebarPanel({
  collapsed,
  onToggle,
  onClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
}) {
  const routes = useAppRoutes();
  const location = useLocation();
  const managedMode = useManagedMode();
  const manageTarget = managedMode.enabled ? managedMode.targets.navigation : null;
  const user = useUser();
  const { navigationLayout } = useCustomerSkin();
  const navigation = createGroupedNavigation(routes, navigationLayout ?? BUILT_IN_NAVIGATION_LAYOUT);
  // `useAppRoutes` has already applied capability, plugin, and tenant-module filtering.
  // Keep the hub separate from product routes without repeating those checks here.
  const adminRoute = routes.find((route) => route.id === 'admin');
  const adminItem = adminRoute
    ? { title: 'Application administration', icon: adminRoute.icon, to: navLinkPath(adminRoute.path) }
    : undefined;
  const entity = selectedEntity(user?.tenants ?? [], location.pathname) ??
    (tenantSlugFromPath(location.pathname) ? undefined : defaultEntity(user?.tenants ?? []));
  const scopeKey = user?.appUserId && entity
    ? JSON.stringify([user.appUserId, entity.tenantId, entity.id])
    : null;
  const tenantAdminPage = entity?.status === 'active' ? firstTenantAdministrationPage(entity.permissions) : undefined;
  const administrationItems: NavItem[] = [
    ...(adminItem ? [adminItem] : []),
    ...(entity && hasTenantPermission(entity.permissions, 'tenant.settings.read')
      ? [{ title: 'Entity administration', icon: Building2Icon, to: tenantPath(entity.slug, 'tenant/entity') }]
      : []),
    ...(entity && tenantAdminPage ? [{ title: 'Tenant administration', icon: LandmarkIcon, to: tenantPath(entity.slug, tenantAdminPage) }] : []),
    ...(user?.isPlatformAdmin ? [{ title: 'Platform administration', icon: ShieldIcon, to: '/platform-admin' }] : [])
  ];

  return (
    <>
      <div className={cn('flex items-center px-2 pt-2', collapsed ? 'justify-center' : 'justify-end')}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose ?? onToggle}
              aria-label={onClose ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="text-sidebar-foreground hover:bg-sidebar-accent dark:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring shrink-0"
            >
              {onClose || !collapsed ? (
                <PanelLeftCloseIcon className="size-4" />
              ) : (
                <PanelLeftOpenIcon className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {onClose ? 'Close' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-2">
          {manageTarget ? <div className={cn('flex', collapsed ? 'justify-center' : 'justify-end')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" size={collapsed ? 'icon' : 'sm'} variant="outline"
                  className="bg-sidebar dark:bg-sidebar text-sidebar-foreground border-sidebar-border dark:border-sidebar-border hover:bg-sidebar-accent dark:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring"
                  aria-label="Manage navigation" onClick={() => {
                    onClose?.();
                    managedMode.openEditor('navigation');
                  }}>
                  <PencilIcon className="size-4" aria-hidden />
                  {!collapsed ? 'Manage navigation' : null}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Manage navigation</TooltipContent>
            </Tooltip>
          </div> : null}
          <SidebarNavigation navigation={navigation} collapsed={collapsed} scopeKey={scopeKey} pathname={location.pathname} />
        </nav>
        {administrationItems.length > 0 ? (
          <div data-sidebar-admin className="max-h-[45vh] shrink-0 overflow-y-auto px-2 pb-2">
            <Separator className="bg-sidebar-border my-2" />
            {!collapsed ? <p className="text-sidebar-foreground px-2 py-1 text-xs font-medium tracking-wide uppercase">Administration</p> : null}
            <div className="flex flex-col gap-0.5">
              {administrationItems.map((item) => <SidebarLink key={item.title} item={item} collapsed={collapsed} />)}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const location = useLocation();
  const previousPath = React.useRef(location.pathname);

  React.useEffect(() => {
    if (location.pathname === previousPath.current) return;
    previousPath.current = location.pathname;
    onMobileClose();
  }, [location.pathname, onMobileClose]);

  return (
    <>
      <aside
        data-collapsed={collapsed}
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border hidden h-full shrink-0 flex-col border-r transition-[width] duration-200 motion-reduce:transition-none md:flex',
          collapsed ? 'w-14' : 'w-60'
        )}
      >
        <SidebarPanel collapsed={collapsed} onToggle={onToggle} />
      </aside>
      <div
        aria-hidden="true"
        onClick={onMobileClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 md:hidden motion-reduce:transition-none',
          mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
      />
      <aside
        aria-label="Navigation"
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r shadow-xl transition-transform duration-200 motion-reduce:transition-none md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarPanel collapsed={false} onToggle={onToggle} onClose={onMobileClose} />
      </aside>
    </>
  );
}
